import { ModelRow, StringUtil } from "@mcleod/core";
import { getDispatchControlValue } from "@mcleod/general";
import { SecondaryFilterProps } from "../FilterPanel";
import { PlanningUtil } from "../PlanningUtil";

export class CustomerServiceSecondaryFilter {
    static readonly APPT_REQUIRED = {
        id: "apptRequired",
        caption: "Appointment Required",
        filter: (row: ModelRow): boolean => {
            return row.get("appt_required_count", 0) > 0 ||
            (["A", "C", "P"].includes(row.getString("move_status")) &&
                ((row.getBoolean("origin_appt_required", false) && !row.getBoolean("origin_appt_confirmed", false)) ||
                    (row.getBoolean("destination_appt_required", false) && !row.getBoolean("destination_appt_confirmed", false))));
        }
    };

    static readonly APPT_CONFIRMED = {
        id: "apptConfirmed",
        caption: "Appointment Confirmed",
        filter: (row: ModelRow): boolean => {
            return row.get("appt_confirmed_count", 0) > 0 ||
                (["A", "C", "P"].includes(row.getString("move_status")) &&
                ((row.getBoolean("origin_appt_required", false) && row.getBoolean("origin_appt_confirmed", false)) ||
                    (row.getBoolean("destination_appt_required", false) && row.getBoolean("destination_appt_confirmed", false))));
        }
    };

    static readonly APPOINTMENT_LATE = {
        id: "isLate",
        caption: "Appointment Late",
        filter: (row: ModelRow): boolean => {
            return this.isLate(row);
        }
    }

    private static isLate(row: ModelRow): boolean {
        return ["A", "C", "P"].includes(row.getString("move_status")) &&
            (this.isLateWarning(row, "origin") || this.isLateWarning(row, "destination"));
    }

    private static isLateWarning(row: ModelRow, stop: "origin" | "destination"): boolean {
        if (row.get(`${stop}_actual_arrival`) != null) return false;

        const diff = PlanningUtil.getTimeDiff(new Date(PlanningUtil.getStopTime(row, stop)));
        if (diff == null) return false;

        const warning = stop === "origin" ? getDispatchControlValue("pickup_warning") : getDispatchControlValue("delivery_warning");
        return diff < (warning * 60 * 60 * 1000);
    }

    static readonly TRACKING_LATE = {
        id: "isTrackingLate",
        caption: "Tracking Late",
        filter: (row: ModelRow): boolean => {
            return ["A", "C", "P"].includes(row.getString("move_status")) && this.isTrackingLate(row)
        }
    }

    private static isTrackingLate(row: ModelRow): boolean {
        if (row.get(`next_stop_actual_arrival`) != null) return false;

        const stopTime: Date = new Date(PlanningUtil.getStopTime(row, "next_stop"));
        const etaToNextStopTimeZone: string = row.get("next_stop_timezone") != null ? " " + row.get("next_stop_timezone") : "";
        const etaToNextStop: Date = new Date(row.getDateTime("last_callin_eta") + etaToNextStopTimeZone);
        const diff = etaToNextStop != null ? etaToNextStop.getTime() - stopTime.getTime() : null;
        if (diff == null) return false;

        return diff > 0;
    }

    static readonly ARRIVED_AT_SHIPPER = {
        id: "arrivedAtShipper",
        caption: "Arrived At Shipper",
        filter: (row: ModelRow): boolean => {
            return !row.isNull("origin_actual_arrival") &&
                row.isNull("origin_actual_departure") &&
                row.get("origin_stop_type") === "PU";
        }
    };

    static readonly ARRIVED_AT_CONSIGNEE = {
        id: "arrivedAtConsignee",
        caption: "Arrived At Consignee",
        filter: (row: ModelRow): boolean => {
            return !row.isNull("destination_actual_arrival") &&
                row.isNull("destination_actual_departure") &&
                row.getString("destination_stop_type", null) === "SO";
        }
    };

    static readonly SHIPMENT_STATUS_ERRORS: SecondaryFilterProps = {
        id: "shipmentStatusError",
        caption: "Shipment Status Error",
        filter: (row: ModelRow): boolean => {
            return row.getBoolean("has_shipment_error", false);
        }
    };

    static readonly INTERCOMPANY: SecondaryFilterProps = {
        id: "hasIntercompany",
        caption: "Intercompany",
        filter: (row: ModelRow): boolean => {
            return row.getBoolean("has_intercompany", false);
        }
         
    };

    static readonly SERVICE_FAILURES = {
        id: "serviceFailure",
        caption: "Service Failure",
        filter: (row: ModelRow): boolean => {
            return row.getBoolean("has_service_failures", false);
        }
    };

    static readonly ON_HOLD = {
        id: "onHold",
        caption: "On Hold",
        filter: (row: ModelRow): boolean => {
            return row.getBoolean("hold", false);
        },
        visible: (data: any) => {
            const profileRow = data as ModelRow;
            return profileRow.getBoolean("include_on_hold_orders", false);
        }
    };

    static readonly LOADS_NOT_ASSIGNED = {
        id: "loadsNotAssigned",
        caption: "Loads Not Assigned",
        filter: (row: ModelRow): boolean => {
            return (row.getBoolean("brokerage") && row.isNull("override_payee_id")) || (!row.getBoolean("brokerage") && row.isNull("tractor_number") && row.isNull("driver1_id"));
        },
        visible: (data: any) => {
            const profileRow = data as ModelRow;
            return profileRow.getBoolean("preassigned_order", false);
        }
    };

    static readonly NOT_READY_TO_BILL = {
        id: "notReadyToBill",
        caption: "Not Ready To Bill",
        filter: (row: ModelRow): boolean => {
            return StringUtil.equalsIgnoreCase("D", row.getString("move_status")) && !row.getBoolean("ready_to_bill", false);
        }
    };

    static readonly LOADS_NOT_RATED = {
        id: "loadsNotRated",
        caption: "Loads Not Rated",
        filter: (row: ModelRow): boolean => {
            return row.isNull("rate");
        }
    };

    static readonly MISSING_BOL = {
        id: "missingBOL",
        caption: "Missing BOL",
        filter: (row: ModelRow): boolean => {
            return !row.getBoolean("bol_received", false);
        }
    }

    static readonly FILTER_FREE_TIME_STARTED = {
        id: "freeTimeStarted",
        caption: "Free Time Started",
        filter: (row: ModelRow) => 
            "F" === row.getString("origin_detention", "")
            || "F" === row.getString("destination_detention", "")
            || row.get("stop_detention_statuses", []).includes("F")
    }

    static readonly FILTER_DETENTION_CHARGING = {
        id: "detentionCharging",
        caption: "Detention Charging",
        filter: (row: ModelRow) => 
            "B" === row.getString("origin_detention", "")
            || "B" === row.getString("destination_detention", "")
            || row.get("stop_detention_statuses", []).includes("B")
    }

    static readonly FILTER_DETENTION_OCCURRED = {
        id: "detentionOccurred",
        caption: "Detention Occurred",
        filter: (row: ModelRow) => 
            "D" === row.getString("origin_detention", "")
            || "D" === row.getString("destination_detention", "")
            || row.get("stop_detention_statuses", []).includes("D")
    }

    static readonly FILTER_DETENTION_CLOSED = {
        id: "detentionClosed",
        caption: "Detention Closed",
        filter: (row: ModelRow) => 
            "C" === row.getString("origin_detention", "")
            || "C" === row.getString("destination_detention", "")
            || row.get("stop_detention_statuses", []).includes("C")
    }
}
